import type { SxProps, Theme } from '@mui/material/styles';

export const madlibStyles: Record<string, SxProps<Theme>> = {
  root: (theme) => ({
    backgroundColor: theme.palette.brand.navy.deep,
    // Dot-grid overlay on the navy, matching Figma.
    backgroundImage:
      'radial-gradient(circle, rgba(255, 255, 255, 0.22) 1.4px, transparent 1.4px)',
    backgroundSize: '22px 22px',
    color: theme.palette.common.white,
    paddingInline: { xs: theme.spacing(3), md: 'clamp(32px, 5vw, 80px)' },
    paddingBlock: { xs: theme.spacing(8), md: 'clamp(88px, 9vw, 152px)' },
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: theme.spacing(3), md: theme.spacing(4) },
    width: '100%',
  }),
  heading: (theme) => ({
    fontFamily: theme.typography.fontFamily,
    fontWeight: theme.typography.fontWeightMedium,
    // Figma: Manrope Medium 48 / 48.
    fontSize: { xs: '2rem', md: '2.25rem', lg: '3rem' },
    lineHeight: { xs: 1.1, lg: '3rem' },
    color: theme.palette.common.white,
    margin: 0,
    maxWidth: 1200,
    marginInline: 'auto',
    width: '100%',
  }),
  prompt: (theme) => ({
    fontFamily: theme.typography.fontFamily,
    fontWeight: theme.typography.fontWeightMedium,
    // Figma: Manrope Medium 24 / 32.784.
    fontSize: { xs: '1.125rem', md: '1.25rem', lg: '1.5rem' },
    lineHeight: { xs: 1.35, lg: '2.049rem' },
    color: theme.palette.brand.mint,
    margin: 0,
    maxWidth: 1200,
    marginInline: 'auto',
    width: '100%',
  }),
  sentence: (theme) => ({
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    // Keep chips snug horizontally, but open up vertical space between
    // the wrapped lines of the sentence.
    columnGap: theme.spacing(1.5),
    rowGap: { xs: theme.spacing(2.5), md: theme.spacing(4) },
    fontFamily: theme.typography.fontFamily,
    fontWeight: theme.typography.fontWeightMedium,
    // Figma: Manrope Medium 32 / 43.712.
    fontSize: { xs: '1.25rem', md: '1.5rem', lg: '2rem' },
    lineHeight: { xs: 1.4, lg: '2.732rem' },
    color: theme.palette.common.white,
    maxWidth: 1200,
    marginInline: 'auto',
    width: '100%',
  }),
  // Pill-styled Select — same config-driven dropdown as the hero, but
  // rendered as a white pill with blue text. Stays white/blue on focus
  // and while open (never inverts to a dark filled state).
  pill: (theme) => ({
    backgroundColor: theme.palette.common.white,
    color: theme.palette.primary.main,
    height: { xs: 40, md: 48 },
    borderRadius: '100px',
    width: 'fit-content',
    display: 'inline-flex',
    alignItems: 'center',
    paddingLeft: theme.spacing(2.5),
    paddingRight: theme.spacing(1),
    fontFamily: theme.typography.fontFamily,
    fontSize: { xs: '0.9375rem', md: '1.125rem' },
    fontWeight: theme.typography.fontWeightMedium,
    '& .MuiSelect-select': {
      color: theme.palette.primary.main,
      padding: 0,
      paddingRight: '30px !important',
      minHeight: 'unset',
      display: 'flex',
      alignItems: 'center',
      backgroundColor: 'transparent',
    },
    // Keep the background transparent (pill stays white) on focus/active.
    '& .MuiSelect-select:focus': { backgroundColor: 'transparent' },
    '& .MuiSelect-icon': {
      color: theme.palette.primary.main,
      right: 10,
    },
  }),
  divider: (theme) => ({
    borderColor: theme.palette.common.white,
    opacity: 1,
    maxWidth: 1200,
    marginInline: 'auto',
    width: '100%',
  }),
  actions: {
    display: 'flex',
    justifyContent: { xs: 'stretch', md: 'flex-end' },
    maxWidth: 1200,
    marginInline: 'auto',
    width: '100%',
  },
};
